import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DEFAULT_PAGE_SIZE,
  Button,
  Card,
  DataTable,
  IconButton,
  Kpi,
  PageHead,
  Pager,
  Select,
  StateBlock,
  type Column,
} from '@w7/ui'
import { usePlStatement, usePlStatementSumOnly } from '@/api/hooks/reports/usePlStatement'
import { useBookmakerSessionFlag } from '@/api/hooks/shared/useStaffMe'
import {
  useAllTm,
  useEventsForPll,
  useGames,
  useMemberBalance,
  useSession,
  useSubGames,
} from '@/app/queries'
import { useViewerCurrency } from '@/api/hooks/shared/useViewerCurrency'
import { AsOf, exportCsv } from '@/app/bits'
import { ExportCsvButton } from '@/app/ExportCsvButton'
import { EMPTY, count, lll, money } from '@/lib/format'
import { inrMajorToDisplay, parseCurrencyType } from '@/lib/currency'
import {
  PL_TYPE_LABEL,
  ROLE_LABEL,
  ROLE_SHORT,
  ROLES,
  type PlTypeName,
  type Role,
} from '@/data/types'
import type { PlStatementRowView } from '@/api/map/plStatement'
import { labAccessLevel } from '@/api/map/plStatement'
import { TYPE_FILTERS } from '@/app/queries'
import { PlStatementDetailDrawer } from './PlStatementDetailDrawer'
import { PlStatementSplitCell } from './PlStatementSplitCell'
import { PlStatementStatusChips } from './plStatementStatusChips'
import {
  FilteredEmpty,
  ReportFilters,
  ScopeBanner,
  ScopeUnavailable,
  scopeSuffix,
  scopedCsvName,
  useReportFilters,
  type Dimension,
} from './ReportFilters'

const PAGE_SIZE = DEFAULT_PAGE_SIZE

/**
 * Member text, Lab Game/getGames, Select Type (`typeName`), Account/getSubGames,
 * and Account/getEventsForPLL all go into the query. Direction/tier chips
 * would lie on an event row.
 */
const DIMENSIONS: Dimension[] = [
  'range',
  'scope',
  'product',
  'typeName',
  'subGame',
  'eventName',
  'member',
  'q',
]

/** Ladder seats shown as money columns, Member first then upline. */
const SPLIT_ROLES: Role[] = [...ROLES].filter((role) => role !== 'player').reverse()

/**
 * PL Statement — Lab `/PLStatement`.
 *
 * One row per settled market, with Member + each ladder seat’s net (the old
 * Player / Master / SM / DM / TM / CM columns). Live data is Agent-API
 * `POST /v1/account/getDownlineAccountsData`. Net total amount comes from
 * `POST /v1/account/getDownlineAccountsSumOnly` (same filters; Lab 1-day cap).
 * The eye button opens Lab Game Detail via `POST /v1/account/getOneAccount`.
 * Multiplier is omitted: it was 1 on every Lab row.
 */
export function PlStatementPage() {
  const filters = useReportFilters(DIMENSIONS)
  const { values, scopeAccount } = filters
  const range = values.range
  const navigate = useNavigate()
  const [selected, setSelected] = useState<PlStatementRowView | null>(null)
  const { data: session } = useSession()
  const viewerCurrency = useViewerCurrency()
  const isCompanyMaster = session?.account.role === 'company-master'
  const { data: tripleMasters = [], isLoading: tripleMastersLoading } = useAllTm(isCompanyMaster)
  const { data: catalogGames = [], isLoading: catalogGamesLoading } = useGames()
  const { data: subGames = [], isLoading: subGamesLoading } = useSubGames(values.product)
  const { data: memberBalance } = useMemberBalance()
  const selectedCurrency = parseCurrencyType(viewerCurrency)
  const productOptions = useMemo(
    () => catalogGames.map((game) => ({ value: game._id, label: game.name })),
    [catalogGames],
  )
  const typeOptions = useMemo(
    () => [
      { value: '', label: 'All' },
      ...TYPE_FILTERS.map((type) => ({
        value: type,
        label: PL_TYPE_LABEL[type],
      })),
    ],
    [],
  )
  const subGameOptions = useMemo(
    () => subGames.map((name) => ({ value: name, label: name })),
    [subGames],
  )
  const productName = useMemo(() => {
    const map = new Map(catalogGames.map((game) => [game._id, game.name]))
    return (id: string) => map.get(id) ?? id
  }, [catalogGames])
  const tmOptions = useMemo(() => {
    const options = tripleMasters.map((tm) => ({ value: tm._id, label: tm.username }))
    if (
      values.scope &&
      scopeAccount &&
      !tripleMasters.some((tm) => tm._id === values.scope)
    ) {
      options.unshift({
        value: values.scope,
        label: `Current scope · ${scopeAccount.username}`,
      })
    }
    return options
  }, [scopeAccount, tripleMasters, values.scope])

  const tmSelected =
    Boolean(isCompanyMaster && values.scope) &&
    (tripleMasters.some((tm) => tm._id === values.scope) ||
      scopeAccount?.role === 'triple-master')

  const eventsQuery = {
    range: { from: range.from, to: range.to },
    ...(values.product ? { game: values.product } : {}),
    ...(values.subGame ? { subGame: values.subGame } : {}),
    ...(values.members.length ? { memberIds: values.members } : {}),
    ...(tmSelected && values.scope ? { tms: values.scope } : {}),
    ...(!tmSelected && values.scope && scopeAccount
      ? { name: { _id: values.scope, accessLevel: labAccessLevel(scopeAccount.role) } }
      : {}),
  }
  const { data: pllEvents = [], isLoading: eventsLoading } = useEventsForPll(eventsQuery)
  const eventOptions = useMemo(
    () => pllEvents.map((name) => ({ value: name, label: name })),
    [pllEvents],
  )

  useEffect(() => {
    if (subGamesLoading || !values.subGame) return
    if (!subGames.includes(values.subGame)) filters.set({ subGame: null, eventName: null })
  }, [filters.set, subGames, subGamesLoading, values.subGame])

  useEffect(() => {
    if (eventsLoading || !values.eventName) return
    if (!pllEvents.includes(values.eventName)) filters.set({ eventName: null })
  }, [eventsLoading, filters.set, pllEvents, values.eventName])

  const bookmakerSessionFlag = useBookmakerSessionFlag()
  const plQuery = {
    range: { from: range.from, to: range.to },
    page: values.page,
    pageSize: PAGE_SIZE,
    ...(values.scope ? { rootId: values.scope } : {}),
    ...(tmSelected && values.scope ? { tms: values.scope } : {}),
    ...(!tmSelected && values.scope && scopeAccount
      ? { name: { _id: values.scope, accessLevel: labAccessLevel(scopeAccount.role) } }
      : {}),
    ...(values.product ? { game: values.product } : {}),
    ...(values.typeName ? { typeName: values.typeName } : {}),
    ...(values.subGame ? { subGame: values.subGame } : {}),
    ...(values.eventName ? { eventName: values.eventName } : {}),
    ...(values.members.length ? { memberIds: values.members } : {}),
    ...(values.q.trim() ? { q: values.q } : {}),
    ...(bookmakerSessionFlag !== 'all' ? { bookmakerSessionFlag } : {}),
  }
  const { data, isLoading, dataUpdatedAt } = usePlStatement(plQuery)
  // SumOnly on page load + every filter change (not pager). Always requested;
  // Agent-API 1-day window failures fall back to the table net.
  const { data: sumOnly } = usePlStatementSumOnly(plQuery)

  const rows = data?.rows ?? []
  // The API reports roles over the full filtered set, so columns cannot jump
  // between pages. The fixed Winner7 order matches the legacy table.
  const splitRoles = useMemo(
    () => SPLIT_ROLES.filter((role) => data?.splitRoles.includes(role)),
    [data?.splitRoles],
  )
  const asOf = data?.asOf ?? (dataUpdatedAt || undefined)
  const page = data?.page ?? values.page
  const total =
    typeof sumOnly?.totalRecords === 'number' && sumOnly.totalRecords > 0
      ? sumOnly.totalRecords
      : (data?.total ?? 0)
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1)
  const netPnl = sumOnly?.netPnl ?? data?.netPnl

  const columns = useMemo<Column<PlStatementRowView>[]>(() => {
    const cols: Column<PlStatementRowView>[] = [
      {
        id: 'row',
        header: '#',
        width: 52,
        cell: (_row, index) => (page - 1) * PAGE_SIZE + index + 1,
      },
      {
        id: 'settled',
        header: 'Date',
        width: 168,
        cell: (row) => <span className="w7-dim">{lll(row.settledAt)}</span>,
        sortValue: (row) => row.settledAt,
      },
      {
        id: 'game',
        header: 'Game',
        hideBelow: 1100,
        cell: (row) => row.game,
        sortValue: (row) => row.game,
      },
      {
        id: 'sport',
        header: 'SubGame',
        hideBelow: 1280,
        help: 'Sport / product under the game bucket — Lab “SubGame”.',
        cell: (row) => <span className="w7-dim">{row.sport}</span>,
        sortValue: (row) => row.sport,
      },
      {
        id: 'event',
        header: 'Event',
        cell: (row) => (
          <span
            className="w7-truncate"
            style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 280 }}
            title={`${row.eventName} — ${row.marketName}`}
          >
            <span>
              {row.eventName}
              <span className="w7-dim"> — {row.marketName}</span>
            </span>
            <PlStatementStatusChips row={row} />
          </span>
        ),
        sortValue: (row) => `${row.eventName} ${row.marketName}`,
      },
      {
        id: 'member',
        header: 'Member',
        help: 'The member’s own result. Positive is green, negative is red — same as Lab.',
        cell: (row) => (
          <PlStatementSplitCell
            row={row}
            role="member"
            currency={row.currencyType ?? selectedCurrency}
          />
        ),
        sortValue: (row) => row.memberPnl,
      },
    ]

    for (const role of splitRoles) {
      cols.push({
        id: `split-${role}`,
        header: ROLE_SHORT[role],
        help: `${ROLE_LABEL[role]} net from the book’s side on this settlement.`,
        hideBelow: role === 'master' ? undefined : 900,
        cell: (row) => (
          <PlStatementSplitCell
            row={row}
            role={role}
            currency={row.currencyType ?? selectedCurrency}
          />
        ),
        sortValue: (row) => row.split[role] ?? 0,
      })
    }

    cols.push({
      id: 'detail',
      header: 'Detail',
      width: 72,
      cell: (row) => (
        <IconButton
          name="eye"
          size="sm"
          label={`Open ${row.marketName} bets for ${row.memberName}`}
          onClick={(event) => {
            event.stopPropagation()
            setSelected(row)
          }}
        />
      ),
    })

    return cols
  }, [page, selectedCurrency, splitRoles])

  const download = () => {
    if (!data) return
    exportCsv(
      scopedCsvName('pl-statement', scopeAccount, range),
      data.rows.map((row) => {
        const rec: Record<string, string | number> = {
          Date: lll(row.settledAt),
          Game: row.game,
          SubGame: row.sport,
          Event: row.eventName,
          Market: row.marketName,
          Member: row.memberName,
          Currency: selectedCurrency,
          'Member P&L': inrMajorToDisplay(row.memberPnl, selectedCurrency),
          'Net to book': inrMajorToDisplay(-row.netAmount, selectedCurrency),
          Turnover: inrMajorToDisplay(row.turnover, selectedCurrency),
          Commission: inrMajorToDisplay(row.commission, selectedCurrency),
        }
        for (const role of splitRoles) {
          rec[ROLE_SHORT[role]] =
            typeof row.split[role] === 'number'
              ? inrMajorToDisplay(row.split[role], selectedCurrency)
              : ''
        }
        return rec
      }),
    )
  }

  const head = (
    <PageHead
      title="PL Statement"
      subtitle={`Settled markets beneath you · ${range.label}${scopeSuffix(scopeAccount)}`}
    />
  )

  const bar = (
    <ReportFilters
      state={filters}
      memberSource="hierarchy"
      textLabel="Filter by event, market, or member"
      textPlaceholder="Event, market, or member"
      omission="Tier and direction are not on this report: each row is one settled market for one member. Game, Sub Game, Type, and Event are Lab Account pickers. Viewing as is the scope chip."
      extra={
        <>
          <span className="w7-pl-tm">
            <span className="w7-pl-tm-label">Game</span>
            <Select
              aria-label="Filter PL Statement by game"
              value={values.product ?? ''}
              placeholder={catalogGamesLoading ? 'Loading games…' : 'All games'}
              disabled={catalogGamesLoading}
              onChange={(event) =>
                filters.set({
                  product: event.target.value || null,
                  subGame: null,
                  eventName: null,
                  typeName: null,
                })
              }
              options={productOptions}
            />
          </span>
          <span className="w7-pl-tm">
            <span className="w7-pl-tm-label">Sub Game</span>
            <Select
              aria-label="Filter PL Statement by sub-game"
              value={values.subGame ?? ''}
              placeholder={subGamesLoading ? 'Loading sub-games…' : 'All sub-games'}
              disabled={subGamesLoading || subGameOptions.length === 0}
              onChange={(event) =>
                filters.set({
                  subGame: event.target.value || null,
                  eventName: null,
                  typeName: null,
                })
              }
              options={subGameOptions}
            />
          </span>
          <span className="w7-pl-tm">
            <span className="w7-pl-tm-label">Type</span>
            <Select
              aria-label="Select Type"
              value={values.typeName ?? ''}
              placeholder="All"
              onChange={(event) => {
                const raw = event.target.value
                filters.set({
                  typeName: TYPE_FILTERS.includes(raw as PlTypeName)
                    ? (raw as PlTypeName)
                    : null,
                })
              }}
              options={typeOptions}
            />
          </span>
          <span className="w7-pl-tm">
            <span className="w7-pl-tm-label">Event</span>
            <Select
              aria-label="Filter PL Statement by event"
              value={values.eventName ?? ''}
              placeholder={eventsLoading ? 'Loading events…' : 'All events'}
              disabled={eventsLoading || eventOptions.length === 0}
              onChange={(event) =>
                filters.set({
                  eventName: event.target.value || null,
                  // Lab `getTypes(formData)` resets Select Type when Event changes.
                  typeName: null,
                })
              }
              options={eventOptions}
            />
          </span>
          {isCompanyMaster ? (
            <span className="w7-pl-tm">
              <span className="w7-pl-tm-label">Triple Master</span>
              <Select
                aria-label="Filter PL Statement by Triple Master"
                value={values.scope ?? ''}
                placeholder={tripleMastersLoading ? 'Loading Triple Masters…' : 'All Triple Masters'}
                disabled={tripleMastersLoading}
                onChange={(event) => filters.set({ scope: event.target.value || null })}
                options={tmOptions}
              />
            </span>
          ) : null}
        </>
      }
      productLabel={productName}
      actions={
        <>
          <AsOf at={asOf} />
          <ExportCsvButton onClick={download} disabled={rows.length === 0} count={rows.length} />
        </>
      }
    />
  )

  if (filters.scopeInvalid) {
    return (
      <>
        {head}
        <ScopeUnavailable onClear={() => filters.clearDimension('scope')} />
      </>
    )
  }

  const provenance = scopeAccount ? (
    <ScopeBanner account={scopeAccount} onClear={() => filters.clearDimension('scope')} />
  ) : null

  if (data?.unavailable) {
    return (
      <>
        {head}
        {bar}
        {provenance}
        <StateBlock icon="pnl" title="PL Statement is not on this API yet">
          {data.unavailable.reason}
          {data.unavailable.trackedBy ? ` Tracked by ${data.unavailable.trackedBy}.` : null}
        </StateBlock>
      </>
    )
  }

  if (data && data.total === 0 && !filters.isFiltered) {
    return (
      <>
        {head}
        {bar}
        {provenance}
        <StateBlock
          icon="pnl"
          title="Nothing settled in this range"
          actions={
            <Button variant="secondary" icon="pnl" onClick={() => navigate('/reports/my-pnl')}>
              My P&L
            </Button>
          }
        >
          Lab PL Statement listed one row per settled market. Widen the dates, or open My P&L for
          the book totals.
        </StateBlock>
      </>
    )
  }

  return (
    <>
      {head}
      {bar}
      {provenance}

      <div className="w7-grid w7-grid-4" style={{ marginBottom: 'var(--w7-s5)' }}>
        <Kpi
          label="Net total amount"
          value={
            data || sumOnly
              ? money(netPnl ?? 0, { currency: selectedCurrency, round: true })
              : EMPTY
          }
          exact={money(netPnl, { currency: selectedCurrency })}
          accent
          tone={
            data || sumOnly
              ? (netPnl ?? 0) === 0
                ? 'neutral'
                : (netPnl ?? 0) > 0
                  ? 'profit'
                  : 'loss'
              : undefined
          }
          meta={
            sumOnly
              ? 'Caller role net from getDownlineAccountsSumOnly across every matching row'
              : 'Book net across every matching row in this range, not only this page'
          }
        />
        <Kpi
          label="Settled rows"
          value={data ? count(total) : EMPTY}
          meta={data?.martStale ? 'Mart marked stale' : 'One row per settled market'}
        />
        <Kpi
          label="This page"
          value={data ? count(rows.length) : EMPTY}
          meta={`${PAGE_SIZE} per page`}
        />
        <Kpi
          label="Available balance"
          value={
            memberBalance
              ? money(memberBalance.available, {
                  currency: selectedCurrency,
                  round: true,
                })
              : EMPTY
          }
          exact={
            memberBalance
              ? money(memberBalance.available, { currency: selectedCurrency })
              : undefined
          }
          meta={
            memberBalance
              ? `Balance ${money(memberBalance.balance, { currency: selectedCurrency })}`
              : 'Member/getBalance'
          }
        />
      </div>

      <Card
        title="PL Statement"
        subtitle="Newest settlements first. Amounts use Lab colouring: positive green, negative red. Ladder columns are that seat’s net on the same settlement."
        flush
        footer={
          <Pager
            page={Math.min(page, pageCount)}
            pageCount={pageCount}
            onChange={filters.setPage}
            total={total}
            pageSize={PAGE_SIZE}
          />
        }
      >
        <DataTable
          label="Profit and loss statement by settled market"
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          loading={isLoading}
          skeletonRows={10}
          initialSort={{ columnId: 'settled', direction: 'desc' }}
          empty={
            filters.isFiltered ? (
              <FilteredEmpty state={filters} icon="pnl" />
            ) : (
              <StateBlock icon="pnl" title="No data found">
                Nothing settled in {range.label}.
              </StateBlock>
            )
          }
        />
      </Card>
      {selected ? (
        <PlStatementDetailDrawer
          row={selected}
          currency={selected.currencyType ?? selectedCurrency}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </>
  )
}
